import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from "@lexical/markdown";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ToolbarPlugin } from "./ToolbarPlugin";
import { ClickableLinkPlugin } from "@lexical/react/LexicalClickableLinkPlugin";
import { buildEditorConfig, MARKDOWN_TRANSFORMERS } from "./utils/config";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot } from "lexical";

export function WYSIWYGEditor({
  content,
  onContentChange,
}: {
  content: string;
  onContentChange: (content: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <LexicalComposer initialConfig={buildEditorConfig({ text: content })}>
      <div className="flex flex-col rounded-md border border-border bg-card">
        <ToolbarPlugin />
        <div className="relative" data-testid="message-wrapper">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                data-testid="message-content-editable"
                className="relative z-1 min-h-[100px] bg-transparent p-4 text-foreground focus:outline-none"
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
            placeholder={
              <div
                data-testid="message-placeholder"
                className="absolute top-4 left-4 z-0 text-muted-foreground"
              >
                {t("bulkMessaging.composeMessage.typeMessage")}
              </div>
            }
            aria-placeholder={t("bulkMessaging.composeMessage.typeMessage")}
          />
        </div>
      </div>
      <MarkdownShortcutPlugin transformers={MARKDOWN_TRANSFORMERS} />
      <OnChangePlugin
        onChange={(state) =>
          state.read(() => {
            const markdownText = $convertToMarkdownString(
              MARKDOWN_TRANSFORMERS,
            );
            onContentChange(markdownText);
          })
        }
      />
      <LinkPlugin />
      <ClickableLinkPlugin newTab={true} />
      <AutoFocusPlugin />
      <FixPositionPlugin text={content} />
    </LexicalComposer>
  );
}

// Checks to see if the text coming in is the same as the text in the editor
// state. This is a pretty hacky way to get the dom to rerender when
// these are dragged and dropped. It wouls be nice if this would be triggered
// by the drag action, but I couldn't figure that out.
// There might be some a way to get drag and drop to work on it's own, too.
function FixPositionPlugin({ text }: { text: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    let currentText = "";
    editor.read(() => {
      currentText = $convertToMarkdownString(MARKDOWN_TRANSFORMERS);
    });

    if (currentText !== text) {
      editor.update(() => {
        $convertFromMarkdownString(
          text ?? "",
          MARKDOWN_TRANSFORMERS,
          undefined,
          true,
        );
        $getRoot().selectEnd();
      });
    }
  }, [editor, text]);

  return <></>;
}
